import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../context/LanguageContext';
import { adminStats } from '../../api';
import { FileText, Users, Mail, MessageSquare, Clock, CheckCircle2, TrendingUp, Building2 } from 'lucide-react';

const statusLabels = {
  in_review: { en: 'In Review', ar: 'قيد المراجعة', color: 'text-amber-400' },
  approved: { en: 'Approved', ar: 'موافق عليه', color: 'text-blue-400' },
  documents_pending: { en: 'Docs Pending', ar: 'مستندات معلقة', color: 'text-orange-400' },
  rejected: { en: 'Rejected', ar: 'مرفوض', color: 'text-rose-400' }
};

const AdminOverview = () => {
  const { lang } = useLang();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminStats().then(setStats).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#111827]/60">…</div>;
  if (!stats) return <div className="text-rose-400">Failed to load</div>;

  const cards = [
    { icon: FileText, label: lang === 'ar' ? 'إجمالي الطلبات' : 'Total Applications', value: stats.total_applications, sub: `+${stats.applications_last_7d} ${lang === 'ar' ? 'هذا الأسبوع' : 'this week'}`, color: 'text-[#3b82f6]', bg: 'from-[#2563eb]/20 to-[#2563eb]/5' },
    { icon: Users, label: lang === 'ar' ? 'المستخدمون' : 'Users', value: stats.total_users, sub: `+${stats.users_last_7d} ${lang === 'ar' ? 'هذا الأسبوع' : 'this week'}`, color: 'text-blue-400', bg: 'from-blue-500/20 to-blue-500/5' },
    { icon: MessageSquare, label: lang === 'ar' ? 'رسائل جديدة' : 'New Messages', value: stats.new_contacts, sub: `${stats.total_contacts} ${lang === 'ar' ? 'إجمالي' : 'total'}`, color: 'text-blue-400', bg: 'from-blue-500/20 to-blue-500/5' },
    { icon: Mail, label: lang === 'ar' ? 'مشتركون' : 'Subscribers', value: stats.total_newsletter, sub: lang === 'ar' ? 'النشرة البريدية' : 'Newsletter', color: 'text-purple-400', bg: 'from-purple-500/20 to-purple-500/5' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{lang === 'ar' ? 'لوحة التحكم' : 'Admin Dashboard'}</h1>
        <p className="text-sm text-[#111827]/60 mt-2">{lang === 'ar' ? 'نظرة شاملة على أداء منصتك' : 'Comprehensive overview of your platform'}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={i} className="glass-card rounded-2xl p-5">
            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.bg} border border-white/10 flex items-center justify-center mb-4`}>
              <c.icon className={`w-5 h-5 ${c.color}`} />
            </div>
            <div className="text-xs uppercase tracking-widest text-[#111827]/50">{c.label}</div>
            <div className="font-display text-3xl text-[#0a0a0a] mt-1">{c.value}</div>
            <div className="text-[10px] text-[#111827]/40 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[#2563eb]" />
            <h2 className="font-display text-lg text-[#0a0a0a]">{lang === 'ar' ? 'توزيع حالات الطلبات' : 'Application Status'}</h2>
          </div>
          <div className="space-y-3">
            {['in_review', 'documents_pending', 'approved', 'rejected'].map((k) => {
              const count = stats.status_breakdown?.[k] || 0;
              const pct = stats.total_applications ? Math.round((count / stats.total_applications) * 100) : 0;
              const s = statusLabels[k];
              return (
                <div key={k}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className={s.color}>{s[lang]}</span>
                    <span className="text-[#111827]/60 text-xs">{count} · {pct}%</span>
                  </div>
                  <div className="h-1.5 bg-[#2563eb]/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#2563eb] to-[#3b82f6]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Zones */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-4 h-4 text-[#2563eb]" />
            <h2 className="font-display text-lg text-[#0a0a0a]">{lang === 'ar' ? 'أكثر المناطق طلباً' : 'Top Free Zones'}</h2>
          </div>
          <div className="space-y-2">
            {stats.top_zones?.length ? stats.top_zones.map((z, i) => (
              <div key={z.code} className="flex items-center justify-between py-2 border-b border-[#2563eb]/8 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="font-display text-[#2563eb] w-6 text-sm">{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-sm text-[#111827]/85">{z.name}</span>
                </div>
                <span className="text-xs text-[#3b82f6] font-mono">{z.count}</span>
              </div>
            )) : <div className="text-xs text-[#111827]/50">{lang === 'ar' ? 'لا يوجد' : 'No data yet'}</div>}
          </div>
        </div>
      </div>

      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-[#2563eb]" />
          <h2 className="font-display text-lg text-[#0a0a0a]">{lang === 'ar' ? 'إجراءات سريعة' : 'Quick Actions'}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/admin/applications?status=in_review" className="px-4 py-3 rounded-xl border border-[#2563eb]/25 hover:bg-[#2563eb]/8 transition-colors text-sm text-[#0a0a0a]">{lang === 'ar' ? 'مراجعة الطلبات' : 'Review Applications'} →</Link>
          <Link to="/admin/contacts?status=new" className="px-4 py-3 rounded-xl border border-[#2563eb]/25 hover:bg-[#2563eb]/8 transition-colors text-sm text-[#0a0a0a]">{lang === 'ar' ? 'رد على الرسائل' : 'Reply to Messages'} →</Link>
          <Link to="/admin/users" className="px-4 py-3 rounded-xl border border-[#2563eb]/25 hover:bg-[#2563eb]/8 transition-colors text-sm text-[#0a0a0a]">{lang === 'ar' ? 'إدارة المستخدمين' : 'Manage Users'} →</Link>
          <Link to="/admin/packages" className="px-4 py-3 rounded-xl border border-[#2563eb]/25 hover:bg-[#2563eb]/8 transition-colors text-sm text-[#0a0a0a]">{lang === 'ar' ? 'تعديل الباقات' : 'Edit Packages'} →</Link>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
